import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { timeEvent, track } from 'shared/analytics'
import { getAnchorIdFromHash } from './useStoAnchors'
import { useStoProject } from './useStoProject'

/**
 * Every STO details page event. Notion: Analytics › Events dictionary › STO details lists each one's
 * properties; add new events there too. `invest_button_clicked`, `redeem_open_clicked` and
 * `sto_document_downloaded` keep the names the legacy page and the dictionary already use.
 */
export type StoEvent =
  | 'sto_page_viewed'
  | 'sto_page_exited'
  | 'sto_section_viewed'
  | 'sto_section_toggled'
  | 'sto_breadcrumb_clicked'
  | 'sto_product_switcher_opened'
  | 'sto_product_switched'
  | 'sto_denomination_selected'
  | 'sto_metric_toggled'
  | 'sto_metric_link_clicked'
  | 'sto_anchor_link_copied'
  | 'sto_chart_toggled'
  | 'sto_chart_range_selected'
  | 'sto_chart_hovered'
  | 'sto_chart_csv_downloaded'
  | 'sto_key_actions_tab_selected'
  | 'sto_key_actions_sheet_opened'
  | 'sto_invest_amount_entered'
  | 'sto_invest_currency_selected'
  | 'invest_button_clicked'
  | 'sto_invest_locked_clicked'
  | 'sto_trade_venue_selected'
  | 'sto_trade_cta_clicked'
  | 'sto_trade_risk_accepted'
  | 'sto_trade_risk_dismissed'
  | 'redeem_open_clicked'
  | 'sto_login_clicked'
  | 'sto_eligibility_cta_clicked'
  | 'sto_how_to_invest_step_clicked'
  | 'sto_support_clicked'
  | 'sto_documents_tab_selected'
  | 'sto_documents_view_all_clicked'
  | 'sto_document_downloaded'
  | 'sto_document_download_failed'
  | 'sto_dividends_view_all_clicked'
  | 'sto_dividend_declaration_clicked'
  | 'sto_next_dividend_clicked'
  | 'sto_performance_tab_selected'
  | 'sto_performance_view_selected'
  | 'sto_nav_period_selected'
  | 'sto_exchange_clicked'
  | 'sto_trading_panel_toggled'
  | 'sto_consideration_toggled'
  | 'sto_key_info_link_clicked'
  | 'sto_updates_view_all_clicked'
  | 'sto_update_link_clicked'
  | 'sto_update_expanded'
  | 'sto_loan_mode_selected'
  | 'sto_loan_amount_entered'
  | 'sto_loan_ltv_selected'
  | 'sto_loan_term_selected'
  | 'sto_loan_offer_clicked'

type StoEventProps = Record<string, string | number | boolean | undefined>

interface TrackedProject {
  name?: string
  type?: string
}

/** Sent with every event: which product and its type, as the rest of the platform's events. */
export const stoProjectProps = (project: TrackedProject | undefined): StoEventProps =>
  project ? { project_name: project.name, project_type: project.type } : {}

/** Analytics must never break the page. */
const send = (event: StoEvent, props: StoEventProps) => {
  try {
    track(event, props)
  } catch (error) {
    console.warn('[sto/analytics] tracking failed', event, error)
  }
}

/** `track` for the STO page: adds the product to every event. */
export function useStoTrack() {
  const { project } = useStoProject()
  return useCallback(
    (event: StoEvent, props: StoEventProps = {}) =>
      send(event, { ...stoProjectProps(project as TrackedProject | undefined), ...props }),
    [project],
  )
}

/** Per product, the sections seen during its current page view, each reported once. Browser only. */
const seenSections = new Map<string, Set<string>>()
const sectionsSeenOn = (slug: string) => {
  const seen = seenSections.get(slug) ?? new Set<string>()
  seenSections.set(slug, seen)
  return seen
}
let isFirstView = true

const scrollDepth = () => {
  const root = document.documentElement
  const scrollable = root.scrollHeight - window.innerHeight
  return scrollable <= 0
    ? 100
    : Math.min(100, Math.round(((window.scrollY + window.innerHeight) / root.scrollHeight) * 100))
}

/** Rounded down to a quarter, so the property stays a short list of values. */
export const depthBucket = (percent: number) => (percent >= 100 ? 100 : Math.floor(percent / 25) * 25)

/**
 * One page view per product: `sto_page_viewed` once it is known, `sto_page_exited` (with Mixpanel's
 * Duration) when the visitor leaves it — another product, another page, or the tab closing.
 */
export function useStoPageTracking() {
  const { slug, project } = useStoProject()
  const { hash } = useLocation()
  const navigationType = useNavigationType()
  const ready = Boolean(project)

  useEffect(() => {
    if (!ready || !project) return undefined
    const props = stoProjectProps(project as TrackedProject)
    seenSections.set(slug, new Set())
    let maxDepth = scrollDepth()
    let sent = false

    send('sto_page_viewed', {
      ...props,
      entry: isFirstView && navigationType === 'POP' ? 'landing' : 'in_app',
      entry_anchor: getAnchorIdFromHash(hash) ?? undefined,
    })
    isFirstView = false
    timeEvent('sto_page_exited')

    const onScroll = () => {
      maxDepth = Math.max(maxDepth, scrollDepth())
    }
    const exit = () => {
      if (sent) return
      sent = true
      send('sto_page_exited', {
        ...props,
        sections_viewed: sectionsSeenOn(slug).size,
        max_scroll_depth: depthBucket(maxDepth),
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('pagehide', exit)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('pagehide', exit)
      exit()
    }
    // One view per product, not one per hash change or refetch: the hash and project are read as it starts.
  }, [slug, ready])
}

/** Seen: half of it on screen, or, for a section taller than that allows, 40% of the screen filled by it. */
const isSeen = (entry: IntersectionObserverEntry) =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= 0.5 ||
    entry.intersectionRect.height >= (entry.rootBounds?.height ?? window.innerHeight) * 0.4)

/** Calls `onSeen` once, the first time the element is actually seen (see `isSeen`). */
export function useSeenOnce(ref: RefObject<HTMLElement | null>, onSeen: () => void, enabled = true) {
  const callback = useRef(onSeen)
  useEffect(() => {
    callback.current = onSeen
  })

  useEffect(() => {
    const element = ref.current
    if (!enabled || !element || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some(isSeen)) return
        observer.disconnect()
        callback.current()
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, enabled])
}

/** `sto_section_viewed` the first time the visitor actually sees the section. */
export function useSectionViewTracking(ref: RefObject<HTMLElement | null>, section: string, enabled = true) {
  const trackSto = useStoTrack()
  const { slug, project } = useStoProject()

  // Read when it fires, not now: the page view starts its list after the sections mount.
  useSeenOnce(
    ref,
    () => {
      const seen = sectionsSeenOn(slug)
      if (seen.has(section)) return
      seen.add(section)
      trackSto('sto_section_viewed', { section })
    },
    enabled && Boolean(project),
  )
}

/** Reports the first hover of a chart only: enough to know who explores it, without an event per move. */
export function useChartHoverTracking(chart: string) {
  const trackSto = useStoTrack()
  const done = useRef(false)
  return useCallback(
    (hovering: unknown) => {
      if (hovering == null || done.current) return
      done.current = true
      trackSto('sto_chart_hovered', { chart })
    },
    [chart, trackSto],
  )
}
